import React, { useMemo } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
} from "chart.js";
import moment from "moment";
import { Skeleton } from "../ui/skeleton";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { AlertTriangleIcon } from "lucide-react";
import { cn } from "../../lib/utils";
import AnalyticsDateFilter, { QuickPeriod } from "./AnalyticsDateFilter";

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip
);

interface FaultAnalyticsData {
  timestamp: number;
  total_requests: number;
  faulty_requests: number;
  fault_rate_percentage: number;
}

interface FaultAnalyticsChartProps {
  data: FaultAnalyticsData[];
  loading: boolean;
  title?: string;
  className?: string;
  onQuickPeriodSelect?: (period: QuickPeriod) => void;
  selectedPeriod?: QuickPeriod | null;
  periodDays?: number;
}

const FaultAnalyticsChart: React.FC<FaultAnalyticsChartProps> = ({
  data,
  loading,
  title = "Fault Rate",
  className,
  onQuickPeriodSelect,
  selectedPeriod,
  periodDays,
}) => {
  const chartData = useMemo(() => {
    
    if (!data || data.length === 0) {
      return {
        labels: [],
        datasets: [],
      };
    }

    // Validate data points
    const validData = data.filter(point => {
      if (!point.timestamp) return false;
      
      return typeof point.fault_rate_percentage === "number" && 
             typeof point.total_requests === "number" &&
             typeof point.faulty_requests === "number" &&
             !isNaN(point.fault_rate_percentage) && 
             !isNaN(point.total_requests) &&
             !isNaN(point.faulty_requests);
    });

    // Dynamic label formatting based on period
    // Convert UTC timestamps to local timezone for display
    const formatLabel = (timestamp: number) => {
      const localTime = moment(timestamp);
      
      if (periodDays && periodDays <= 1) {
        // For 24 hours or less, determine optimal format based on data span
        if (validData.length <= 1) {
          return localTime.format("h:mm A");
        }
        
        // Calculate time span of the data
        const timestamps = validData.map(d => d.timestamp).sort((a, b) => a - b);
        const timeSpanHours = (timestamps[timestamps.length - 1] - timestamps[0]) / (1000 * 60 * 60);
        
        if (timeSpanHours <= 6) {
          // For 6 hours or less, show hour:minute
          return localTime.format("h:mm A");
        } else if (timeSpanHours <= 12) {
          // For 6-12 hours, show hour:minute but less frequently
          const minutes = localTime.minutes();
          if (minutes === 0 || minutes === 30) {
            return localTime.format("h:mm A");
          } else {
            return localTime.format("h A");
          }
        } else {
          // For 12-24 hours, show hour only but ensure we have enough granularity
          return localTime.format("h A");
        }
      } else {
        // For more than 24 hours, show only days in local timezone
        return localTime.format("MMM D");
      }
    };

    // Use fault rate percentage directly
    const faultRateData = validData.map(point => point.fault_rate_percentage);
    

    return {
      labels: validData.map(point => formatLabel(point.timestamp)),
      datasets: [
        {
          label: "Fault Rate %",
          data: faultRateData,
          backgroundColor: "rgba(239, 68, 68, 0.8)", // Red for fault rate
          borderColor: "#ef4444",
          borderWidth: 1,
          borderRadius: 4,
          borderSkipped: false,
        },
      ],
    };
  }, [data, periodDays]);

  const options = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      intersect: false,
      mode: "index" as const,
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: "rgba(0, 0, 0, 0.8)",
        titleColor: "#ffffff",
        bodyColor: "#ffffff",
        borderColor: "#ef4444",
        borderWidth: 1,
        cornerRadius: 6,
        displayColors: false,
        callbacks: {
          title: function(context: any) {
            const dataIndex = context[0].dataIndex;
            if (data && data[dataIndex]) {
              return moment(data[dataIndex].timestamp).format("MMM D, YYYY [at] h:mm A");
            }
            return context[0].label;
          },
          label: function(context: any) {
            const dataIndex = context.dataIndex;
            const dataPoint = data[dataIndex];
            const faultRate = context.parsed.y.toFixed(2);
            
            return [
              `Fault Rate: ${faultRate}%`,
              `Total Requests: ${dataPoint.total_requests}`,
              `Faulty Requests: ${dataPoint.faulty_requests}`,
            ];
          },
        },
      },
    },
    scales: {
      x: {
        type: "category" as const,
        grid: {
          display: false,
        },
        ticks: {
          color: "#737373", // muted-foreground
          font: {
            size: 12,
          },
          maxTicksLimit: periodDays && periodDays <= 1 ? 12 : 8,
        },
      },
      y: {
        beginAtZero: true,
        max: 100, // Fault rate is always 0-100%
        grid: {
          color: "rgba(115, 115, 115, 0.1)", // muted with transparency
        },
        ticks: {
          color: "#737373", // muted-foreground
          font: {
            size: 12,
          },
          callback: function(value: any) {
            return `${value}%`;
          },
          stepSize: 10, // Show increments of 10%
        },
      },
    },
  }), [data]);

  if (loading) {
    return (
      <Card className={cn("border-border", className)}>
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-lg font-gc-med text-foreground">
            <AlertTriangleIcon className="w-5 h-5 text-red-500" />
            {title}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="h-[250px] flex items-center justify-center">
            <div className="space-y-3 w-full">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-4 w-1/2" />
              <Skeleton className="h-32 w-full" />
              <div className="grid grid-cols-4 gap-2">
                <Skeleton className="h-3 w-full" />
                <Skeleton className="h-3 w-full" />
                <Skeleton className="h-3 w-full" />
                <Skeleton className="h-3 w-full" />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Card className={cn("border-border", className)}>
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-lg font-gc-med text-foreground">
              <AlertTriangleIcon className="w-5 h-5 text-red-500" />
              {title}
              {periodDays && (
                <span className="text-sm font-gc-reg text-muted-foreground">
                  {periodDays === 1 ? "Last 24 hours" :
                   periodDays === 7 ? "Last week" : 
                   periodDays === 30 ? "Last month" :
                   `Last ${periodDays} days`}
                </span>
              )}
            </CardTitle>
            {onQuickPeriodSelect && (
              <AnalyticsDateFilter
                onQuickPeriodSelect={onQuickPeriodSelect}
                selectedPeriod={selectedPeriod}
              />
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="h-[250px] flex items-center justify-center">
            <div className="text-center space-y-3">
              <h3 className="text-lg font-gc-med text-foreground">
                No fault rate data available
              </h3>
              <p className="text-muted-foreground max-w-md">
                Fault rate analytics will appear here once your application has evaluation results.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={cn("border-border", className)}>
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-lg font-gc-med text-foreground">
            <AlertTriangleIcon className="w-5 h-5 text-red-500" />
            {title}
            {periodDays && (
              <span className="text-sm font-gc-reg text-muted-foreground">
                {periodDays === 1 ? "Last 24 hours" :
                 periodDays === 7 ? "Last week" : 
                 periodDays === 30 ? "Last month" :
                 `Last ${periodDays} days`}
              </span>
            )}
          </CardTitle>
          {onQuickPeriodSelect && (
            <AnalyticsDateFilter
              onQuickPeriodSelect={onQuickPeriodSelect}
              selectedPeriod={selectedPeriod}
            />
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="h-[250px]">
          {loading ? (
            <div className="space-y-3">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-4/5" />
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-2/3" />
              <Skeleton className="h-4 w-4/5" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-4 w-5/6" />
              <Skeleton className="h-4 w-full" />
            </div>
          ) : (
            <Bar data={chartData} options={options} />
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default FaultAnalyticsChart;